import type { Topic } from "./types";

export const CONNECTORS: Topic = {
  slug: "voegwoorden",
  title: "Voegwoorden & verbindingswoorden",
  tagline: "omdat, want, daarna, hoewel — connectors that glue A2 sentences together.",
  duration: "16 min read",
  summary:
    "The connector words DUO Deel 3 and Deel 4 require: causal (omdat/want), sequence (eerst/toen/daarna/tenslotte), time (als/toen/voordat), and the dat-clause. Each comes with a word-order rule.",
  // Deel 1's rider ("vertel ook waarom…") often needs omdat/want; Deel 2
  // descriptions sometimes use 'omdat' for reasoning; Deel 3 + 4 require
  // connectors explicitly per the rubric. So all four deelen.
  relevantDeelen: [1, 2, 3, 4],
  relevantCriteria: ["ad", "gr"],
  sections: [
    { kind: "heading", level: 2, text: "Why connectors matter" },
    {
      kind: "paragraph",
      text: "A2 Spreken Deel 3 explicitly demands a causal marker (omdat or want) — the rubric calls it out by name. Deel 4 demands at least two time markers AND one causal marker across the whole story. Without these, your score drops a full point on Adequaatheid no matter how good your vocabulary is. Connectors are not optional dressing; they are graded items.",
    },
    {
      kind: "callout",
      tone: "rule",
      title: "The big idea",
      text: "Every connector belongs to ONE of three groups. The group decides where the verb goes. Get the group right, the verb position follows automatically.",
    },

    { kind: "heading", level: 2, text: "The three groups" },
    {
      kind: "paragraph",
      text: "Dutch connectors split into three syntactic families. They look similar but trigger different word orders. Mixing them up is the #1 A2 word-order mistake.",
    },
    {
      kind: "table",
      headers: ["Group", "What happens", "Examples"],
      emphasizeFirstCol: true,
      rows: [
        [
          "Onderschikkend (sub-clause)",
          "Verb sails to the END of the clause",
          "omdat, dat, als, toen, terwijl, voordat, nadat, hoewel, of, zodra",
        ],
        [
          "Nevenschikkend (coordinating)",
          "Normal V2 stays — both clauses act independent",
          "want, en, maar, of (or), dus",
        ],
        [
          "Bijwoordelijk (adverb-like)",
          "Triggers V2 INVERSION — verb still slot 2, subject moves",
          "eerst, toen, daarna, vervolgens, tenslotte, daarom, dus",
        ],
      ],
    },
    {
      kind: "callout",
      tone: "warning",
      title: "Same word, different group",
      text: "toen and dus appear in two groups depending on how you use them. toen as 'when (in the past)' is sub-clause; toen as 'then' is adverbial. dus as 'so' between clauses is coordinating; dus at the start of a sentence is adverbial.",
    },

    { kind: "heading", level: 2, text: "Reden — Cause and reason" },
    {
      kind: "paragraph",
      text: "Four words mean roughly 'because' or 'so' in English, but each one wants a different word order. Memorise the pair (word + order) as one unit.",
    },
    {
      kind: "table",
      headers: ["Word", "Group", "Pattern", "Example"],
      emphasizeFirstCol: true,
      rows: [
        [
          "omdat",
          "sub-clause",
          "verb at end",
          "Ik blijf thuis omdat ik ziek ben.",
        ],
        [
          "want",
          "coordinating",
          "normal V2",
          "Ik blijf thuis want ik ben ziek.",
        ],
        [
          "doordat",
          "sub-clause",
          "verb at end",
          "Hij komt te laat doordat de bus niet rijdt.",
        ],
        [
          "dus",
          "coordinating / adverbial",
          "V2 (inversion if first)",
          "Het regent, dus ik blijf thuis.",
        ],
        [
          "daarom",
          "adverbial",
          "V2 inversion",
          "Het regent. Daarom blijf ik thuis.",
        ],
      ],
    },
    {
      kind: "callout",
      tone: "tip",
      title: "omdat vs want",
      text: "Same meaning, choose by feel: omdat sounds slightly more formal and is what DUO graders expect to hear AT LEAST once in Deel 3 and Deel 4. Drill omdat.",
    },
    {
      kind: "examples",
      rows: [
        {
          dutch: "Ik leer Nederlands omdat ik in Nederland woon.",
          english: "I learn Dutch because I live in the Netherlands. (omdat → woon at the end)",
        },
        {
          dutch: "Ik leer Nederlands want ik woon in Nederland.",
          english: "Same meaning. (want → normal V2)",
        },
        {
          dutch: "Het regent, daarom neem ik de bus.",
          english: "It's raining, that's why I take the bus. (daarom triggers inversion: neem ik)",
        },
      ],
    },
    {
      kind: "callout",
      tone: "warning",
      title: "Classic A2 trap",
      text: "*Ik blijf thuis omdat ik ben ziek.* — wrong. After omdat the verb GOES TO THE END. Drill aloud: omdat-ik-iets-iets-VERB.",
    },

    { kind: "heading", level: 2, text: "Tijd — Time connectors" },
    {
      kind: "paragraph",
      text: "Time connectors say WHEN something happens. Three of them are easy to confuse: als, toen, and wanneer. The choice depends on whether the time is past, present, or general.",
    },
    {
      kind: "table",
      headers: ["Word", "When to use it", "Example"],
      emphasizeFirstCol: true,
      rows: [
        [
          "als",
          "general / future / repeating",
          "Als het regent, blijf ik thuis. (whenever, every time)",
        ],
        [
          "toen",
          "ONE specific moment in the past",
          "Toen ik klein was, woonde ik in Turkije.",
        ],
        [
          "wanneer",
          "in questions, or formal time",
          "Wanneer kom je? / Ik weet niet wanneer hij komt.",
        ],
        [
          "terwijl",
          "two things happening at once",
          "Terwijl ik kook, kijkt hij tv.",
        ],
        [
          "voordat",
          "before",
          "Voordat we eten, wassen we onze handen.",
        ],
        [
          "nadat",
          "after",
          "Nadat we hebben gegeten, gaan we wandelen.",
        ],
        [
          "zodra",
          "as soon as",
          "Zodra hij komt, beginnen we.",
        ],
      ],
    },
    {
      kind: "callout",
      tone: "rule",
      title: "als vs toen",
      text: "If it happened ONCE in the past → toen. If it happens generally / repeatedly / in the future → als. 'Toen ik student was…' (one period in the past) vs 'Als ik moe ben…' (every time, general).",
    },
    {
      kind: "paragraph",
      text: "All time connectors above are sub-clause connectors — the verb goes to the end of the clause they introduce.",
    },
    {
      kind: "examples",
      rows: [
        {
          dutch: "Voordat ik naar mijn werk ga, drink ik koffie.",
          english: "Before I go to work, I drink coffee. (verb 'ga' at the end of the voordat-clause)",
        },
        {
          dutch: "Toen ik in Amsterdam woonde, fietste ik elke dag.",
          english: "When I lived in Amsterdam, I cycled every day. (toen + verb-final)",
        },
        {
          dutch: "Ik bel je zodra ik thuis ben.",
          english: "I'll call you as soon as I'm home.",
        },
      ],
    },

    { kind: "heading", level: 2, text: "Volgorde — Sequence words for Deel 4" },
    {
      kind: "paragraph",
      text: "These are the connectors DUO grades you on in Deel 4 (the three-photo story). At least TWO time markers from this list, plus one causal marker (omdat/want/dus), is the rubric's hard floor for full Adequaatheid. They are all adverbial, which means they sit in slot 1 and trigger V2 INVERSION — the verb stays in slot 2, the subject moves.",
    },
    {
      kind: "table",
      headers: ["Word", "Meaning", "Example"],
      emphasizeFirstCol: true,
      rows: [
        ["eerst", "first", "Eerst sta ik op."],
        ["toen", "then (sequence, not 'when')", "Toen ging hij naar de markt."],
        ["daarna", "after that", "Daarna eet ik ontbijt."],
        ["vervolgens", "next, subsequently", "Vervolgens drinken we koffie."],
        ["tenslotte / ten slotte", "finally", "Tenslotte gaan we naar bed."],
        ["eindelijk", "finally (after waiting)", "Eindelijk komt de bus."],
      ],
    },
    {
      kind: "callout",
      tone: "tip",
      title: "Deel 4 template that scores 9/9",
      text: "\"Eerst [photo 1 sentence]. Toen [photo 2 sentence]. Tenslotte [photo 3 sentence], omdat [reason].\" That's 4 sequence markers + 1 causal — a clean rubric pass.",
    },
    {
      kind: "examples",
      rows: [
        {
          dutch: "Eerst staat de man op. Toen drinkt hij koffie. Daarna fietst hij naar werk. Tenslotte werkt hij op kantoor, omdat hij zijn baan leuk vindt.",
          english: "Full Deel-4 narrative — 4 time markers, 1 causal marker, present tense, simple sentences.",
        },
      ],
    },
    {
      kind: "callout",
      tone: "warning",
      title: "Watch the inversion",
      text: "After eerst, toen, daarna, tenslotte, eindelijk — the verb stays in slot 2 and the subject FLIPS. *Eerst ik ga* is wrong. *Eerst ga ik* is right.",
    },

    { kind: "heading", level: 2, text: "Tegenstelling — Contrast & concession" },
    {
      kind: "table",
      headers: ["Word", "Group", "Example"],
      emphasizeFirstCol: true,
      rows: [
        [
          "maar",
          "coordinating (V2)",
          "Ik wil komen, maar ik heb geen tijd.",
        ],
        [
          "hoewel",
          "sub-clause (verb at end)",
          "Hoewel het regent, fietsen we toch.",
        ],
        [
          "terwijl",
          "sub-clause (verb at end)",
          "Terwijl jij rust, werk ik.",
        ],
        [
          "ondanks dat",
          "sub-clause (verb at end)",
          "Ondanks dat hij ziek is, gaat hij naar werk.",
        ],
      ],
    },
    {
      kind: "callout",
      tone: "tip",
      title: "When to use which",
      text: "maar is the safest A2 contrast. hoewel sounds more written but is graded well in Deel 3 reasoning. terwijl is for two things at once, NOT a contrast — be careful.",
    },

    { kind: "heading", level: 2, text: "Dat — That-clauses" },
    {
      kind: "paragraph",
      text: "Dat introduces a sub-clause that reports a thought, fact, or opinion. After verbs like denken, weten, vinden, zeggen, hopen, geloven — dat is the connector. The verb of the dat-clause goes to the end.",
    },
    {
      kind: "examples",
      rows: [
        { dutch: "Ik denk dat het gaat regenen.", english: "I think it's going to rain." },
        { dutch: "Hij zegt dat hij morgen komt.", english: "He says he's coming tomorrow." },
        { dutch: "Ik weet dat zij in Utrecht woont.", english: "I know that she lives in Utrecht." },
        { dutch: "We hopen dat het mooi weer wordt.", english: "We hope the weather will be nice." },
      ],
    },
    {
      kind: "callout",
      tone: "tip",
      title: "Of vs dat",
      text: "of replaces dat when the embedded thought is a yes/no question. \"Ik weet niet of hij komt.\" = \"I don't know whether he's coming.\" Same word-order rule (verb at the end).",
    },
    {
      kind: "examples",
      rows: [
        { dutch: "Weet je of de bus al komt?", english: "Do you know if the bus is coming yet?" },
        { dutch: "Ik vraag me af of hij thuis is.", english: "I wonder whether he's home." },
      ],
    },

    { kind: "heading", level: 2, text: "Sentence formulae you can reuse" },
    {
      kind: "paragraph",
      text: "These are the templates A2 students lean on for Deel 3 reasoning and Deel 4 narratives. Slot in your own nouns and verbs; the connectors stay put.",
    },
    {
      kind: "formula",
      title: "Cause with omdat (verb to the END)",
      pattern: "[hoofdzin], omdat + [onderwerp] + [middel] + [werkwoord aan het einde].",
      examples: [
        { dutch: "Ik ga niet uit, omdat ik moe ben.", english: "I'm not going out because I'm tired." },
        { dutch: "Ze koopt een fiets, omdat ze elke dag naar werk fietst.", english: "She buys a bike because she cycles to work every day." },
        { dutch: "We blijven thuis, omdat het regent.", english: "We're staying home because it's raining." },
      ],
    },
    {
      kind: "formula",
      title: "Cause with want (normal V2)",
      pattern: "[hoofdzin], want + [onderwerp] + [werkwoord] + [reden].",
      examples: [
        { dutch: "Ik ga niet uit, want ik ben moe.", english: "Same meaning as the omdat version, but no verb-final flip." },
        { dutch: "We blijven thuis, want het regent.", english: "We're staying home because it's raining." },
      ],
    },
    {
      kind: "formula",
      title: "Deel 3 — preference + reason",
      pattern: "Ik vind + [foto A] + [vergelijkende vorm] dan + [foto B], omdat + [onderwerp] + [reden] + [werkwoord aan het einde].",
      legend: [
        { token: "[vergelijkende vorm]", meaning: "leuker, gezonder, mooier, beter, goedkoper, duurzamer, …" },
      ],
      examples: [
        { dutch: "Ik vind koken thuis gezonder dan eten in een restaurant, omdat je weet wat je eet.", english: "I think cooking at home is healthier than eating in a restaurant, because you know what you're eating." },
        { dutch: "Ik vind de fiets leuker dan de auto, omdat fietsen goed voor de gezondheid is.", english: "I prefer the bike because cycling is good for health." },
      ],
    },
    {
      kind: "formula",
      title: "Deel 4 — three-photo story (9/9 template)",
      pattern: "Eerst + [werkwoord] + [onderwerp] + [foto 1]. Toen + [werkwoord] + [onderwerp] + [foto 2]. Tenslotte + [werkwoord] + [onderwerp] + [foto 3], omdat + [onderwerp] + [reden] + [werkwoord aan het einde].",
      legend: [
        { token: "[werkwoord]", meaning: "use simple PRESENT tense — A2 doesn't need past tense for this" },
      ],
      examples: [
        { dutch: "Eerst staat de man op. Toen drinkt hij koffie. Tenslotte fietst hij naar werk, omdat hij zijn baan leuk vindt.", english: "Four time markers + one causal — full rubric pass." },
        { dutch: "Eerst gaat de vrouw naar de winkel. Toen kookt ze het eten. Tenslotte eten ze samen, omdat het feest is.", english: "Same shape, different topic." },
      ],
    },
    {
      kind: "formula",
      title: "Time clause with als",
      pattern: "Als + [onderwerp] + [middel] + [werkwoord aan het einde], + [hoofdzin].",
      legend: [
        { token: "Als", meaning: "general / future / repeating — not a one-time past event (use 'toen' for that)" },
      ],
      examples: [
        { dutch: "Als het regent, blijf ik thuis.", english: "If/when it rains, I stay home." },
        { dutch: "Als ik moe ben, drink ik koffie.", english: "When(ever) I'm tired, I drink coffee." },
      ],
    },
    {
      kind: "formula",
      title: "Reported thought / opinion (dat-clause)",
      pattern: "Ik denk / vind / weet / hoop + dat + [onderwerp] + [middel] + [werkwoord aan het einde].",
      examples: [
        { dutch: "Ik denk dat het morgen mooi weer wordt.", english: "I think the weather will be nice tomorrow." },
        { dutch: "Ik vind dat Nederlands moeilijk is.", english: "I find Dutch difficult." },
        { dutch: "Ik hoop dat hij op tijd komt.", english: "I hope he arrives on time." },
      ],
    },

    { kind: "heading", level: 2, text: "Common A2 traps" },
    {
      kind: "list",
      items: [
        "*omdat ik ben ziek* — wrong. After omdat the verb goes to the END: omdat ik ziek BEN.",
        "*Eerst ik ga naar school* — wrong. eerst is adverbial; verb in slot 2: Eerst GA ik naar school.",
        "Mixing toen and als. Past once → toen. General/future → als. \"Toen ik moe was\" not \"Als ik moe was.\"",
        "Using want with a verb at the end. want is coordinating — keep normal V2 after it.",
        "Forgetting the causal marker entirely in Deel 3 / Deel 4. The rubric requires it; an answer without omdat or want loses a full point.",
        "Using two clauses without any connector. \"Ik blijf thuis. Ik ben ziek.\" — graded as A1. Add the connector: \"Ik blijf thuis omdat ik ziek ben.\" — A2.",
      ],
    },

    { kind: "heading", level: 2, text: "Quick checklist before the exam" },
    {
      kind: "list",
      items: [
        "Three groups: sub-clause (verb to end), coordinating (normal V2), adverbial (V2 with inversion).",
        "Cause: omdat (sub) / want (coord) / daarom (adv) / dus (adv or coord).",
        "Past-once → toen (sub). General/future → als (sub). Question-of-time → wanneer.",
        "Deel 4 must include ≥2 of: eerst / toen / daarna / vervolgens / tenslotte. All adverbial — verb in slot 2 with subject inversion.",
        "Deel 3 must include omdat or want at least once. Drill omdat.",
        "After dat the verb goes to the end. Same for of in indirect yes/no questions.",
      ],
    },
  ],
};
